import { Container, ContainerHeader, ContainerContent } from 'components/Containers/Containers'
import { Flex, ThinSeparator } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IOperationPlanState, ITaskCycleBindingConfigState } from 'types/operationPlans.interface'
import { formatDateForDisplay } from 'utils/helpers'
import { cantKeepUpOptions, findWording, nextPhaseStartOptions, workAheadOptions, workKindOptions } from '../planWording'

export const Review = ({ state, handleSave, setStep }: { state: IOperationPlanState, handleSave: () => void, setStep: (step: number) => void }) => {

    const handleBack = () => setStep(5)

    const dueWording = state.dueAt ? `by ${formatDateForDisplay(state.dueAt)}` : 'with no deadline — continuous'
    const phaseNames = state.phases.map((p) => p.name).filter(Boolean).join(', ')
    const summarySentence = `You're planning to move ${state.targetValue ?? '?'} ${state.targetUnit?.label ?? ''} at ${state.site?.label ?? 'no site yet'} ${dueWording}, in ${state.phases.length} phase(s)${phaseNames ? `: ${phaseNames}` : ''}.`

    return <>
        <Flex column gap='1rem' >
            <Container>
                <ContainerHeader>
                    <TitleText>CHECK & CREATE</TitleText>
                </ContainerHeader>
                <ContainerContent>
                    <Flex column gap='1rem'>
                        <BodyText weight='600' >{summarySentence}</BodyText>
                        <OtherText grey>The plan is created switched off (Draft). Activate it when you&apos;re ready — the system then starts creating work.</OtherText>
                    </Flex>
                </ContainerContent>
            </Container>
            <Container>
                <ContainerHeader>
                    <TitleText>WHAT WE ARE DOING</TitleText>
                </ContainerHeader>
                <ContainerContent>
                    <Flex column gap='1rem'>
                        <Flex gap='5rem' >
                            <BodyText grey >NAME</BodyText>
                            <BodyText  >{state.name} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >CODE</BodyText>
                            <BodyText  >{state.code || '-'} </BodyText>
                        </Flex>
                    </Flex>
                </ContainerContent>
            </Container>
            <Container>
                <ContainerHeader>
                    <TitleText>WHERE & WHO</TitleText>
                </ContainerHeader>
                <ContainerContent>
                    <Flex column gap='1rem'>
                        <Flex gap='5rem' >
                            <BodyText grey >SITE</BodyText>
                            <BodyText  >{state.site?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >OWNER</BodyText>
                            <BodyText  >{state.owner?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >AREA LEVEL</BodyText>
                            <BodyText  >{state.zoneScopeLevel?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >AREAS</BodyText>
                            <BodyText  >{state.zoneScopeIds?.length ? state.zoneScopeIds.map((z) => z.label).join(', ') : '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >DEPARTMENT</BodyText>
                            <BodyText  >{state.department?.label || '-'} </BodyText>
                        </Flex>
                    </Flex>
                </ContainerContent>
            </Container>
            <Container>
                <ContainerHeader>
                    <TitleText>THE GOAL</TitleText>
                </ContainerHeader>
                <ContainerContent>
                    <Flex column gap='1rem'>
                        <Flex gap='5rem' >
                            <BodyText grey >PRIORITY</BodyText>
                            <BodyText  >{state.priority?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >TARGET</BodyText>
                            <BodyText  >{state.targetValue || '-'} {state.targetUnit?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >PROGRESS SOURCE</BodyText>
                            <BodyText  >{state.progressSourceField?.label || 'Not tracked'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >BUDGET</BodyText>
                            <BodyText  >{state.targetBudgetCost || '-'} {state.targetCurrencyCode?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >DUE DATE</BodyText>
                            <BodyText  >{state.dueAt ? formatDateForDisplay(state.dueAt) : 'No deadline — runs continuously'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >WHO SEES IT ON DASHBOARDS</BodyText>
                            <BodyText  >{state.boardVisibility?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >AUTO-PAUSE ON SERIOUS (L3+) INCIDENT</BodyText>
                            <BodyText  >{state.autoPauseOnL3Plus ? 'Yes' : 'No'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >OBJECTIVE</BodyText>
                            <BodyText  >{state.objective || '-'} </BodyText>
                        </Flex>
                    </Flex>
                </ContainerContent>
            </Container>
            <Container>
                <ContainerHeader>
                    <TitleText>THE PHASES</TitleText>
                </ContainerHeader>
                {state.phases.length ? state.phases.map((p, k) => <ContainerContent key={p.id}>
                    <Flex column gap='1rem'>
                        <Flex gap='5rem' >
                            <BodyText grey >PHASE</BodyText>
                            <BodyText  >{p.sequence ? `${p.sequence} · ${p.name || '-'}` : p.name || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >KIND OF WORK</BodyText>
                            <BodyText  >{findWording(workKindOptions, p.executableType?.value) || p.executableType?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >NEXT PHASE STARTS</BodyText>
                            <BodyText  >{findWording(nextPhaseStartOptions, p.advanceRule?.value) || p.advanceRule?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >WORK TEMPLATE</BodyText>
                            <BodyText  >{p.workflowTemplate?.label || p.processTemplate?.label || p.taskCycleTemplate?.label || '-'} </BodyText>
                        </Flex>
                        {p.executableType?.value === 1 && <>
                            <Flex gap='5rem' >
                                <BodyText grey >RUNNING AT ONCE</BodyText>
                                <BodyText  >{(p.bindingConfig as ITaskCycleBindingConfigState)?.parallelismTarget || '-'} </BodyText>
                            </Flex>
                            <Flex gap='5rem' >
                                <BodyText grey >PRIORITY WHEN RESOURCES ARE SHORT</BodyText>
                                <BodyText  >{(p.bindingConfig as ITaskCycleBindingConfigState)?.weight || '-'} </BodyText>
                            </Flex>
                            <Flex gap='5rem' >
                                <BodyText grey >IF WE CAN&apos;T KEEP UP</BodyText>
                                <BodyText  >{findWording(cantKeepUpOptions, (p.bindingConfig as ITaskCycleBindingConfigState)?.degradeRule?.value) || (p.bindingConfig as ITaskCycleBindingConfigState)?.degradeRule?.label || '-'} </BodyText>
                            </Flex>
                        </>}
                        {k < state.phases.length - 1 && <ThinSeparator />}
                    </Flex>
                </ContainerContent>) : <Flex mt='16' mb='16' column gap='1rem'>
                    <BodyText grey style={{ textAlign: 'center' }} >No phases added</BodyText>
                </Flex>}
            </Container>

            <Container>
                <ContainerHeader>
                    <TitleText>HOW THE WORK FLOWS</TitleText>
                </ContainerHeader>
                <ContainerContent >
                    <Flex column gap='1rem'>
                        <Flex gap='5rem' >
                            <BodyText grey >PREPARE WORK</BodyText>
                            <BodyText  >{findWording(workAheadOptions, state.horizon.horizonType?.value) || state.horizon.horizonType?.label || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >HOW FAR AHEAD</BodyText>
                            <BodyText  >{state.horizon.horizonValue || '-'} {state.horizon.horizonValue ? (state.horizon.horizonType?.value === 2 ? 'shifts ahead' : 'hours ahead') : ''} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >HOW OFTEN TO CHECK (MINUTES)</BodyText>
                            <BodyText  >{state.horizon.schedulerRunFreqMin || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >TOP UP WHEN FEWER THAN</BodyText>
                            <BodyText  >{state.horizon.replenishThreshold || '-'} </BodyText>
                        </Flex>
                        <Flex gap='5rem' >
                            <BodyText grey >NEVER QUEUE MORE THAN</BodyText>
                            <BodyText  >{state.horizon.maxQueueSize || '-'} </BodyText>
                        </Flex>
                    </Flex>
                </ContainerContent>
            </Container>

        </Flex>
        <Flex gap='1rem' mt='16' alignEnd justend>
            <Button variant='secondary' onClick={handleBack}>Back</Button>
            <Button variant='primary' onClick={handleSave} >Save</Button>
        </Flex>
    </>
}